import checkLimits from './check-limits';
import formatNumber from './format-number';
import { findNearestLimit, getIncrementSteps } from './member-limits';
import { monthlyPrice } from './price-for-period';
import {
    PLAN_BLOCKER_CATEGORIES,
    SUBDIRECTORY_ADDON_NAME,
    SUBDIRECTORY_IDENTIFIER,
} from './constants';

/**
 * @typedef {import('./check-limits').LimitValidation} LimitValidation
 * @typedef {object} AddonPrice
 * @property {string} billing_period
 * @property {number} cost
 * @property {string[]} limits
 * @property {string} stripe_price_id
 */

/**
 * @typedef {object} Addon
 * @property {string} name
 * @property {AddonPrice[]} prices
 */

/**
 * @typedef {object} Limit
 * @property {string} name
 * @property {number|string|boolean} value
 */

/**
 * @typedef {object} SiteLimitProperties
 * @property {boolean} exceeded
 * @property {number} limit
 * @property {number?} total
 * @property {string?} active
 * @property {boolean?} hasOfficialTheme
 */

/**
 * @typedef {(typeof PLAN_BLOCKER_CATEGORIES)[keyof typeof PLAN_BLOCKER_CATEGORIES]} BlockerCategory
 */

/**
 * @typedef {object} PlanBlocker
 * @property {string} limit - the limit/type key (e.g. 'members', 'customThemes', 'subdirectory', 'currentPlan')
 * @property {BlockerCategory} category - drives how the blocker is presented:
 *   self-serve resolves in a settings screen, audience needs a bigger plan,
 *   structural routes to support, current-plan is a card-state discriminator
 *   (never a requirement)
 * @property {string|null} message - the single-line reason copy
 */

/**
 * Maps each blocker's `limit` key to its category. Unknown keys fall back to
 * self-serve, the safest presentation (a plain list item).
 *
 * @type {Record<string, BlockerCategory>}
 */
const BLOCKER_CATEGORY_BY_LIMIT = {
    customThemes: PLAN_BLOCKER_CATEGORIES.SELF_SERVE,
    limitAnalytics: PLAN_BLOCKER_CATEGORIES.SELF_SERVE,
    staff: PLAN_BLOCKER_CATEGORIES.SELF_SERVE,
    newsletters: PLAN_BLOCKER_CATEGORIES.SELF_SERVE,
    limitAutomations: PLAN_BLOCKER_CATEGORIES.SELF_SERVE,
    customIntegrations: PLAN_BLOCKER_CATEGORIES.SELF_SERVE,
    limitStripeConnect: PLAN_BLOCKER_CATEGORIES.SELF_SERVE,
    customSendingDomain: PLAN_BLOCKER_CATEGORIES.SELF_SERVE,
    members: PLAN_BLOCKER_CATEGORIES.AUDIENCE,
    [SUBDIRECTORY_IDENTIFIER]: PLAN_BLOCKER_CATEGORIES.STRUCTURAL,
    ghostVersion: PLAN_BLOCKER_CATEGORIES.STRUCTURAL,
    currentPlan: PLAN_BLOCKER_CATEGORIES.CURRENT_PLAN,
};

/**
 * @typedef {Record<string, SiteLimitProperties>} SiteLimits
 */

/**
 * @typedef {string[]} Features
 */

/**
 * @typedef {object} Price
 * @property {number} cost
 * @property {string} stripe_price_id
 * @property {string} billing_period
 * @property {boolean} active
 * @property {Limit[]} limits
 * @property {string} nickname
 * @property {string} base_product
 * @property {boolean} legacy
 * @property {string} group
 * @property {Features} [features]
 */

/**
 * @typedef {object} Product
 * @property {string} name
 * @property {string} type
 * @property {number} active
 * @property {number} order
 * @property {object} [metadata]
 * @property {Price[]} [prices]
 * @property {Addon[]} [addons]
 * @property {Limit[]} [limits]
 * @property {string} stripeId
 * @property {Features} [features]
 */

/**
 * @typedef {object} ProcessAddonsOptions
 * @property {Addon[]} addons
 * @property {boolean} isSubdirectory
 * @property {'month'|'year'} showPeriod
 */

/**
 * @typedef {object} GetPriceAndLimitsOptions
 * @property {Price[]} suitablePrices
 * @property {SiteLimits} currentSiteLimits
 * @property {string} productName
 * @property {Limit[]} limits
 */

/**
 * @typedef {object} GetFeaturesOptions
 * @property {Price} suitablePrice
 * @property {number} maxMembersForPrice
 * @property {Features} features
 * @property {boolean} isLastStepAndNotEntryProduct
 * @property {string} productName
 * @property {Price[]} prices
 * @property {'month'|'year'} showPeriod
 * @property {AddonPrice[]} availableAddonPrices
 * @property {boolean} isSubdirectory
 * @property {object[]} availableProducts
 */

/**
 * @typedef {object} ShouldDisableProductOptions
 * @property {Price|null} suitablePrice
 * @property {{base_product?: string, stripe_price_id?: string}|null} currentPrice
 * @property {Price[]} prices
 * @property {'month'|'year'} showPeriod
 * @property {boolean} isSubdirectory
 * @property {{major: number}|null} currentGhostVersion
 * @property {string} productName
 * @property {LimitValidation[]} limitsValidations
 * @property {AddonPrice[]} availableAddonPrices
 * @property {boolean} hasCustomSendingDomain
 * @property {object[]} availableProducts
 */

/**
 * @typedef {object} CalculateTotalCostOptions
 * @property {Price} suitablePrice
 * @property {'month'|'year'} showPeriod
 * @property {AddonPrice[]} availableAddonPrices
 * @property {string} productName
 * @property {boolean} isSubdirectory
 * @property {Price[]} prices
 * @property {boolean} _verbose
 */

/**
 * @typedef {object} IProductUtils
 * @property {(ProcessAddonsOptions) => {name: string, cost: number}[]|[]} processAddons
 * @property {(GetPriceAndLimitsOptions) => {suitablePrice: Price|null, limitsValidations: LimitValidation[]}} getPriceAndLimits
 * @property {({prices: Price[], showPeriod: 'month'|'year'}) => Price|null} getFallbackPrice
 * @property {({price: Price, features: Features, isLastStepAndNotEntryProduct: boolean}) => Features} sortFeatures
 * @property {(GetFeaturesOptions) => {productFeatures: Features, addonFeatures: Features}} getFeatures
 * @property {({currentMembers: number, productName: string}) => boolean} isLastStepAndNotEntryProduct
 * @property {({currentMembers: number}) => boolean} isLastIncrementStep
 * @property {(ShouldDisableProductOptions) => {disableProduct: boolean, validationMsg: string|null, blockers: PlanBlocker[]}} shouldDisableProduct
 * @property {(CalculateTotalCostOptions) => string} calculateTotalCost
 */

/** @implements {IProductUtils} */
class ProductUtils {
    /**
     * @private
     * @param {{addons: Addon[], name: string, enforce?: boolean, period: string}} options
     * @returns {{name: string, price: number}|null}
     */
    #getAddonPriceAndName = ({ addons, name, enforce = false, period }) => {
        const addonItem =
            addons.filter((addon) => addon.name.toLowerCase().indexOf(name) >= 0)?.[0] ?? null;

        if (addonItem && enforce) {
            const addonPrice =
                addonItem.prices.filter((price) => price.billing_period === period)?.[0] ?? null;

            if (addonPrice) {
                return {
                    name: addonItem.name,
                    price: addonPrice.cost,
                };
            }
        }

        return null;
    };

    /**
     * @param {ProcessAddonsOptions} options
     * @returns {{name: string, cost: number}[]|[]} availableAddonPrices
     */
    processAddons = ({ addons, isSubdirectory, showPeriod }) => {
        const subdirectoryAddon = this.#getAddonPriceAndName({
            addons,
            name: SUBDIRECTORY_IDENTIFIER,
            enforce: isSubdirectory,
            period: showPeriod,
        });

        let availableAddonPrices = [];

        if (subdirectoryAddon) {
            availableAddonPrices.push(subdirectoryAddon);
        }

        return availableAddonPrices;
    };

    /**
     * @param {GetPriceAndLimitsOptions} options
     * @returns {{suitablePrice: Price|null, limitsValidations: LimitValidation[]}}
     */
    getPriceAndLimits = ({ suitablePrices, currentSiteLimits, productName, limits }) => {
        // The prices are already sorted by member limits, so we can safely pick the first one
        const suitablePrice = suitablePrices?.[0] ?? null;

        // Check any suitable price against limits that would affect the price
        const limitsValidations = checkLimits(suitablePrice, {
            currentSiteLimits,
            product: productName,
            productLimits: limits,
        });

        return {
            suitablePrice,
            limitsValidations,
        };
    };

    /**
     * @param {{prices: Price[], showPeriod: 'month'|'year'}} options
     * @returns {Price|null}
     */
    getFallbackPrice = ({ prices, showPeriod }) => {
        const filteredPricesForBillingPeriod = prices.filter(
            (price) => price.billing_period === showPeriod,
        );

        // Filter prices to only those with member limits for sorting
        const pricesWithMemberLimits = filteredPricesForBillingPeriod.filter((price) => {
            const memberLimit = price.limits?.find((limit) => limit.name === 'members');
            return !!memberLimit;
        });

        // If we have prices with member limits, sort and return the highest
        if (pricesWithMemberLimits.length > 0) {
            return pricesWithMemberLimits.sort(({ limits: a }, { limits: b }) => {
                const [aMembers] = a.filter((limit) => limit.name === 'members');
                const [bMembers] = b.filter((limit) => limit.name === 'members');

                return bMembers.value - aMembers.value;
            })[0];
        }

        // If no prices have member limits, return the first price for the period
        return filteredPricesForBillingPeriod[0] ?? null;
    };

    /**
     * @param {{price: Price, features: Features, isLastStepAndNotEntryProduct: boolean, availableProducts: object[]}} options
     * @returns {Features}
     */
    sortFeatures = ({
        price,
        features,
        isLastStepAndNotEntryProduct,
        productName,
        availableProducts,
    }) => {
        price?.features?.forEach((feature) => {
            if (feature.indexOf('members') >= 0) {
                if (isLastStepAndNotEntryProduct) {
                    const incrementSteps = getIncrementSteps(availableProducts);
                    feature = formatNumber(incrementSteps[incrementSteps.length - 1]) + '+ members';
                }
            }

            // put members price feature on last in the array
            features.push(feature);
        });

        return features;
    };

    /**
     * @param {GetFeaturesOptions} options
     * @returns {{productFeatures: Features, addonFeatures: Features}}
     */
    getFeatures = ({
        suitablePrice,
        maxMembersForPrice,
        features,
        isLastStepAndNotEntryProduct,
        productName,
        prices,
        showPeriod,
        availableAddonPrices,
        isSubdirectory,
        availableProducts,
    }) => {
        let productFeatures = features?.map((feature) => feature);
        /** @type {Features} */
        let addonFeatures = [];
        const fallBackPrice = this.getFallbackPrice({ prices, showPeriod });

        if (suitablePrice && maxMembersForPrice) {
            productFeatures = this.sortFeatures({
                price: maxMembersForPrice,
                features: productFeatures,
                isLastStepAndNotEntryProduct,
                productName,
                availableProducts,
            });
        } else if (fallBackPrice) {
            productFeatures = this.sortFeatures({
                price: fallBackPrice,
                features: productFeatures,
                isLastStepAndNotEntryProduct,
                productName,
                availableProducts,
            });
        }

        if (isSubdirectory && availableAddonPrices?.length) {
            availableAddonPrices.forEach((addon) => {
                // We currently only support those three addons
                const addonFeatureName =
                    addon?.name?.toLowerCase().indexOf(SUBDIRECTORY_IDENTIFIER) >= 0 &&
                    `Managed ${SUBDIRECTORY_ADDON_NAME.toLowerCase()}`;

                addonFeatures.push(addonFeatureName);
            });
        }

        return {
            productFeatures,
            addonFeatures,
        };
    };

    /**
     * @param {{currentMembers: number, productName: string, availableProducts: object[]}} options
     * @returns {boolean}
     */
    isLastStepAndNotEntryProduct({ currentMembers, productName, availableProducts }) {
        const isLastIncrementStep = this.isLastIncrementStep({ currentMembers, availableProducts });
        // Products are sorted by order, with the lowest (entry) product first
        return (
            isLastIncrementStep &&
            availableProducts[0].name?.toLowerCase() !== productName?.toLowerCase()
        );
    }

    /**
     * @param {{currentMembers: number, availableProducts: object[]}} options
     * @returns {boolean}
     */
    isLastIncrementStep({ currentMembers, availableProducts }) {
        const incrementSteps = getIncrementSteps(availableProducts);
        return (
            findNearestLimit(currentMembers, availableProducts) ===
            incrementSteps[incrementSteps.length - 1]
        );
    }

    /**
     * Collects every reason the product is not selectable, tagged with a
     * `limit` key and a category (see `BLOCKER_CATEGORY_BY_LIMIT`). The list is
     * ordered by display precedence: `blockers[0].message` is the single
     * reason the plan card has always shown (per-limit validations first,
     * then sending domain, Ghost version, subdirectory, the members
     * fallback, and last the current-plan state).
     *
     * @param {ShouldDisableProductOptions} options
     * @returns {{disableProduct: boolean, validationMsg: string|null, blockers: PlanBlocker[]}}
     */
    shouldDisableProduct({
        suitablePrice,
        currentPrice,
        prices,
        showPeriod,
        isSubdirectory,
        currentGhostVersion,
        productName,
        limitsValidations,
        availableAddonPrices,
        hasCustomSendingDomain,
        availableProducts,
    }) {
        const currentPriceId = currentPrice?.stripe_price_id;
        const entryProductName = availableProducts?.[0]?.name?.toLowerCase();

        /** @type {PlanBlocker[]} */
        const blockers = [];

        // Block the product if any of the limits are reached (product or price)
        if (limitsValidations && limitsValidations.length >= 1) {
            limitsValidations.forEach((validation) => {
                blockers.push({
                    limit: validation.limit,
                    category:
                        BLOCKER_CATEGORY_BY_LIMIT[validation.limit] ??
                        PLAN_BLOCKER_CATEGORIES.SELF_SERVE,
                    message: validation.message,
                });
            });
        }

        if (
            hasCustomSendingDomain &&
            productName.toLowerCase() === entryProductName &&
            currentPrice?.base_product?.toLowerCase() !== entryProductName
        ) {
            blockers.push({
                limit: 'customSendingDomain',
                category: BLOCKER_CATEGORY_BY_LIMIT.customSendingDomain,
                message: `Not available with active custom sending domain`,
            });
        }

        // Block the entry plan for Ghost versions before 4.x
        if (currentGhostVersion?.major < 4 && productName.toLowerCase() === entryProductName) {
            blockers.push({
                limit: 'ghostVersion',
                category: BLOCKER_CATEGORY_BY_LIMIT.ghostVersion,
                message: `Not available with older Ghost versions`,
            });
        }

        if (isSubdirectory && !availableAddonPrices?.length) {
            blockers.push({
                limit: SUBDIRECTORY_IDENTIFIER,
                category: BLOCKER_CATEGORY_BY_LIMIT[SUBDIRECTORY_IDENTIFIER],
                message: `Not available with active subdirectory`,
            });
        }

        if (!suitablePrice && prices?.length) {
            const fallBackPrice = this.getFallbackPrice({ prices, showPeriod });

            // Set a validation message like we do in checkLimits, so we're consistent
            blockers.push({
                limit: 'members',
                category: BLOCKER_CATEGORY_BY_LIMIT.members,
                message: `Not available with more than ${formatNumber(fallBackPrice?.limits[0].value)} members`,
            });
        }

        if (suitablePrice && currentPriceId === suitablePrice?.stripe_price_id) {
            // The price the user is currently subscribed to: a card-state
            // discriminator, never a requirement to resolve
            blockers.push({
                limit: 'currentPlan',
                category: BLOCKER_CATEGORY_BY_LIMIT.currentPlan,
                message: `This is your current plan and billing period`,
            });
        }

        return {
            disableProduct: blockers.length > 0,
            validationMsg: blockers[0]?.message ?? null,
            blockers,
        };
    }

    /**
     * @param {CalculateTotalCostOptions} options
     * @returns {string}
     */
    calculateTotalCost = ({
        suitablePrice,
        showPeriod,
        availableAddonPrices,
        productName,
        isSubdirectory,
        prices,
        _verbose,
    }) => {
        let totalCost = '0';

        if (suitablePrice) {
            if (isSubdirectory && availableAddonPrices?.length) {
                let addonPriceTotal = 0;
                const basePrice = monthlyPrice(suitablePrice?.cost, suitablePrice?.billing_period);

                availableAddonPrices.forEach((addon) => {
                    const addonPrice = monthlyPrice(addon?.cost, addon?.billing_period);
                    // Add the cost for the subdirectory to the shown price of the product
                    addonPriceTotal += addonPrice;
                });
                totalCost = formatNumber(basePrice + addonPriceTotal);
            } else {
                totalCost = formatNumber(
                    monthlyPrice(suitablePrice?.cost, suitablePrice?.billing_period),
                );
            }
        } else if (!suitablePrice && prices?.length >= 1) {
            // CASE: We're above the possible members limit for this product and no prices are available
            //       Just so we have a number to show (which is disabled anyway), find the highest price
            //       available and return that price instead.
            const fallBackPrice = this.getFallbackPrice({ prices, showPeriod });

            if (_verbose) {
                const memberLimit = fallBackPrice?.limits?.find(
                    (limit) => limit.name === 'members',
                );
                console.log(
                    `[BMA] → fallback price for ${productName}:`,
                    fallBackPrice?.stripe_price_id,
                    memberLimit?.value ?? 'no limit',
                    ' members',
                );
            }

            totalCost = formatNumber(
                monthlyPrice(fallBackPrice?.cost, fallBackPrice?.billing_period),
            );
        }

        return totalCost;
    };
}

const productUtils = new ProductUtils();

export default productUtils;
